/**
 * EventBroadcastAdmin — putting a conference session on air.
 *
 * Organisers had no screen of their own: an event could be streamed only by
 * whoever edited the event record, and the watch link was stored without ever
 * being shown to anybody. This is the same broadcast panel the church uses,
 * with the event's own start and stop around it.
 *
 * A multi-day conference does not need a second event for day two — start a
 * session, end it, and start the next one. The event stays one record.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Radio, Loader2, Video, Link2, ExternalLink, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { BroadcastMethods } from '@/components/live/BroadcastMethods';
import { BroadcastInfoEditor } from '@/components/live/BroadcastInfoEditor';

export const EventBroadcastAdmin = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [watchLink, setWatchLink] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['event-live', eventId],
    queryFn: () => api.get(`/events/${eventId}/live`).then((r) => r.data),
    refetchInterval: 20000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['event-live', eventId] });

  const start = useMutation({
    mutationFn: (useCamera) => api.post(`/events/${eventId}/live/start`, {
      title, use_camera: useCamera, online_url: useCamera ? null : watchLink || null,
    }),
    onSuccess: () => { toast.success('This session is on air'); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not start the session'),
  });

  const stop = useMutation({
    mutationFn: () => api.post(`/events/${eventId}/live/stop`),
    onSuccess: () => { toast.success('Session ended'); refresh(); },
    onError: () => toast.error('Could not end the session'),
  });

  const live = !!data?.live;

  return (
    <div className="space-y-4" data-testid="event-broadcast-admin">
      <div>
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">Livestream</h2>
        <p className="mt-1 text-sm text-slate-500">
          Put this event on air for people who cannot be in the room. Each day or
          session is started and ended here — the event itself stays one record.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline"
                   className={live ? 'border-rose-200 bg-rose-50 text-rose-700' : 'text-slate-500'}
                   data-testid="event-live-status">
              <Radio className={`mr-1 h-3 w-3 ${live ? 'animate-pulse' : ''}`} />
              {live ? 'On air' : 'Off air'}
            </Badge>
            {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            {live && data?.transport && (
              <span className="text-xs text-slate-500">
                {data.transport === 'livekit' ? 'broadcasting through FWM' : 'external provider'}
              </span>
            )}
            {live && (
              <Button size="sm" variant="outline" className="ml-auto"
                      onClick={() => stop.mutate()} disabled={stop.isPending}
                      data-testid="event-live-stop">
                End session
              </Button>
            )}
          </div>

          {!live && (
            <>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="What is this session called? e.g. Day 2 — Morning plenary"
                     data-testid="event-live-title" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => start.mutate(true)} disabled={start.isPending}
                        data-testid="event-live-start-camera">
                  <Video className="mr-2 h-4 w-4" />Start through FWM
                </Button>
                <div className="flex min-w-[240px] flex-1 gap-2">
                  <Input value={watchLink} onChange={(e) => setWatchLink(e.target.value)}
                         placeholder="…or paste a YouTube / Facebook watch link"
                         data-testid="event-live-link" />
                  <Button variant="outline" disabled={!watchLink || start.isPending}
                          onClick={() => start.mutate(false)}
                          data-testid="event-live-start-link">
                    <Link2 className="mr-2 h-4 w-4" />Use link
                  </Button>
                </div>
              </div>
            </>
          )}

          <BroadcastMethods scope="event" ident={eventId} />
          <EventDestination eventId={eventId} />
        </CardContent>
      </Card>

      <BroadcastInfoEditor scope="event" ident={eventId} label="event" />

      <p className="text-xs text-slate-500">
        Viewers watch on the event page.{' '}
        <a href={`/events/${eventId}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 underline" data-testid="event-live-public-link">
          Open it <ExternalLink className="h-3 w-3" />
        </a>{' '}
        For a private event, only people on the guest list can watch.
      </p>
    </div>
  );
};

export const EventDestination = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');

  const { data } = useQuery({
    queryKey: ['event-live-destination', eventId],
    queryFn: () => api.get(`/events/${eventId}/live/destination`).then((r) => r.data),
    enabled: open,
  });

  useEffect(() => { if (data) setUrl(data.rtmp_server_url || ''); }, [data]);

  const save = useMutation({
    mutationFn: () => api.put(`/events/${eventId}/live/destination`, {
      rtmp_server_url: url.trim(), rtmp_stream_key: key.trim(),
    }),
    onSuccess: ({ data: out }) => {
      setKey('');
      toast.success(out.configured ? 'OBS can now stream this event' : 'Destination cleared');
      queryClient.invalidateQueries({ queryKey: ['broadcast-methods', 'event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event-live-destination', eventId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'That destination could not be saved'),
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="event-destination">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center gap-2 text-left"
              data-testid="event-destination-toggle">
        <KeyRound className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-xs font-medium text-slate-700">
          Your own streaming destination
        </span>
        <span className="ml-auto text-[11px] text-slate-400">
          {data?.has_key ? 'saved' : open ? 'hide' : 'set up'}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] leading-relaxed text-slate-500">
            Paste your YouTube, Facebook or Restream server and key here, and OBS
            can stream this conference even where FWM's own ingest is not
            available. The key is saved for the encoder, never shown on any
            public page, and never returned once saved — only replaced.
          </p>
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
                 placeholder="rtmp://a.rtmp.youtube.com/live2"
                 data-testid="event-destination-url" />
          <Input value={key} onChange={(e) => setKey(e.target.value)}
                 type="password" autoComplete="off"
                 placeholder={data?.has_key ? 'Key saved — type a new one to replace it' : 'Stream key'}
                 data-testid="event-destination-key" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}
                    data-testid="event-destination-save">
              {save.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
              Save destination
            </Button>
            {(data?.has_key || url) && (
              <Button size="sm" variant="ghost" className="text-rose-600"
                      onClick={() => { setUrl(''); setKey(''); save.mutate(); }}
                      data-testid="event-destination-clear">
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventBroadcastAdmin;
